import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Search, Target, CheckCircle2, XCircle, MapPin, Building2, Loader2,
  Zap, RefreshCw, ThumbsUp, ThumbsDown, Mail, MailCheck, Users,
  Landmark, Settings2, ChevronDown,
} from 'lucide-react';

const ScoreBar = ({ score }) => {
  const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 70 ? 'text-emerald-400' : score >= 40 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-bold ${textColor}`}>{score}</span>
    </div>
  );
};

export default function ProspectionH24Tab() {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDept, setSearchDept] = useState('');
  const [searchType, setSearchType] = useState('tous');
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState('nouveau');
  const [emailOnly, setEmailOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [configOpen, setConfigOpen] = useState(false);
  const [configQueries, setConfigQueries] = useState('');
  const [configDepts, setConfigDepts] = useState('');
  const [configCommunes, setConfigCommunes] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter) params.append('statut', filter);
      if (emailOnly) params.append('email_only', 'true');
      const [pRes, sRes] = await Promise.all([
        api.get(`/prospection-h24/prospects?${params}`),
        api.get('/prospection-h24/stats'),
      ]);
      setProspects(pRes.data?.prospects || []);
      setStats(sRes.data || {});
    } catch (e) {
      console.error(e);
    }
  }, [filter, emailOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = async () => {
    if (!searchQuery.trim() && searchType !== 'commune') return;
    setSearching(true);
    try {
      const res = await api.post('/prospection-h24/recherche-manuelle', {
        query: searchQuery,
        departement: searchDept || undefined,
        type: searchType,
      });
      const found = res.data?.prospects || [];
      const withEmail = found.filter(p => p.email_verifie).length;
      toast.success(`${found.length} prospect(s) trouves, ${withEmail} avec email verifie`);
      setFilter('nouveau');
      await fetchData();
    } catch (e) {
      toast.error('Erreur recherche');
    }
    setSearching(false);
  };

  const handleAction = async (prospectId, action) => {
    try {
      await api.post('/prospection-h24/action', { prospect_id: prospectId, action });
      toast.success(action === 'valide' ? 'Valide et ajoute au CRM' : `Prospect ${action}`);
      await fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    try {
      await api.post('/prospection-h24/action-bulk', {
        prospect_ids: Array.from(selected), action,
      });
      toast.success(`${selected.size} prospect(s) ${action === 'valide' ? 'valides' : action}`);
      setSelected(new Set());
      await fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleAutoConfig = async () => {
    try {
      const res = await api.post('/prospection-h24/config-auto');
      toast.success(`Config auto: ${res.data?.description || 'OK'}`);
      await fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const saveConfig = async () => {
    try {
      await api.post('/prospection-h24/config', {
        queries: configQueries.split('\n').filter(q => q.trim()),
        departements: configDepts.split(',').map(d => d.trim()).filter(Boolean),
        search_communes: configCommunes,
      });
      toast.success('Prospection H24 configuree');
      setConfigOpen(false);
      await fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  return (
    <div className="space-y-4" data-testid="prospection-h24-tab">
      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {[
          { label: 'Total', value: stats.total || 0, color: 'text-blue-500' },
          { label: 'Nouveaux', value: stats.nouveaux || 0, color: 'text-amber-500' },
          { label: 'Valides', value: stats.valides || 0, color: 'text-emerald-500' },
          { label: 'Avec email', value: stats.avec_email || 0, color: 'text-violet-500', icon: MailCheck },
          { label: 'Score moy.', value: stats.score_moyen || 0, color: 'text-purple-500' },
          { label: 'Rejetes', value: stats.rejetes || 0, color: 'text-red-500' },
        ].map((s, i) => (
          <Card key={i} className="border">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</p>
              <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <Card className="border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="vignoble, GAEC maraichage, cooperative, commune..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="prospect-search-input"
              />
            </div>
            <Input
              placeholder="Dept (26, 84...)"
              value={searchDept}
              onChange={(e) => setSearchDept(e.target.value)}
              className="w-28"
              data-testid="prospect-dept-input"
            />
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="w-36 rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="tous">Tous</option>
              <option value="entreprise">Entreprises</option>
              <option value="commune">Communes</option>
            </select>
            <Button onClick={handleSearch} disabled={searching} className="gap-2" data-testid="prospect-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Chercher
            </Button>
          </div>

          {/* Config buttons */}
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={handleAutoConfig} className="gap-1" data-testid="auto-config-btn">
              <Settings2 className="w-3 h-3" /> Config auto
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfigOpen(!configOpen)} className="gap-1">
              <Target className="w-3 h-3" /> Config manuelle
            </Button>
            {stats.config_active && (
              <Badge variant="outline" className="text-xs text-emerald-600">
                H24 actif : {stats.config_active.criteres?.queries?.length || 0} mots-cles
              </Badge>
            )}
          </div>

          {configOpen && (
            <div className="mt-4 p-4 rounded-lg bg-muted/30 border space-y-3">
              <p className="text-sm font-medium">Configuration prospection automatique</p>
              <div>
                <label className="text-xs text-muted-foreground">Mots-cles (un par ligne)</label>
                <textarea
                  className="w-full mt-1 p-2 rounded bg-background border text-sm min-h-[80px]"
                  placeholder={"exploitation agricole\nvignoble Drome\nGAEC maraichage\ncooperative"}
                  value={configQueries}
                  onChange={(e) => setConfigQueries(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Departements (virgule)</label>
                  <Input placeholder="26, 84, 07, 30" value={configDepts} onChange={(e) => setConfigDepts(e.target.value)} />
                </div>
                <div className="flex items-center gap-2 pt-4">
                  <input type="checkbox" checked={configCommunes} onChange={(e) => setConfigCommunes(e.target.checked)} className="rounded" />
                  <label className="text-sm">Prospecter les communes (ETA)</label>
                </div>
              </div>
              <Button onClick={saveConfig} size="sm" className="gap-2">
                <CheckCircle2 className="w-3 h-3" /> Activer
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          {[
            { key: 'nouveau', label: 'Nouveaux', count: stats.nouveaux },
            { key: 'valide', label: 'Valides', count: stats.valides },
            { key: 'rejete', label: 'Rejetes', count: stats.rejetes },
            { key: 'contacte', label: 'Contactes', count: stats.contactes },
          ].map(f => (
            <Button key={f.key} variant={filter === f.key ? 'default' : 'ghost'} size="sm"
              onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}>
              {f.label} {f.count > 0 && <Badge variant="secondary" className="ml-1 text-xs">{f.count}</Badge>}
            </Button>
          ))}
          <Button variant={emailOnly ? 'default' : 'ghost'} size="sm" onClick={() => setEmailOnly(!emailOnly)}
            className="gap-1" data-testid="filter-email">
            <MailCheck className="w-3 h-3" /> Email verifie
          </Button>
        </div>
        {selected.size > 0 && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => handleBulkAction('valide')} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="bulk-validate">
              <ThumbsUp className="w-3 h-3" /> Valider ({selected.size})
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleBulkAction('rejete')} className="gap-1" data-testid="bulk-reject">
              <ThumbsDown className="w-3 h-3" /> Rejeter ({selected.size})
            </Button>
          </div>
        )}
      </div>

      {/* Prospects list */}
      <div className="space-y-2">
        {prospects.length > 0 && (
          <div className="flex items-center gap-2 px-2">
            <input type="checkbox"
              checked={selected.size === prospects.length && prospects.length > 0}
              onChange={() => selected.size === prospects.length ? setSelected(new Set()) : setSelected(new Set(prospects.map(p => p.id)))}
              className="w-4 h-4 rounded" />
            <span className="text-xs text-muted-foreground">Tout ({prospects.length})</span>
          </div>
        )}

        {prospects.map(p => {
          const isExpanded = expandedId === p.id;
          const typeIcon = p.type_prospect === 'commune' ? Landmark : Building2;
          const TypeIcon = typeIcon;
          return (
            <Card key={p.id} className={`border transition-all hover:shadow-sm ${selected.has(p.id) ? 'ring-1 ring-primary/50 bg-primary/5' : ''}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} className="w-4 h-4 mt-1 rounded" />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : p.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <TypeIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-semibold text-sm truncate">{p.nom}</span>
                      <ScoreBar score={p.score || 0} />
                      {p.email_verifie && <Badge className="bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[10px] gap-1"><MailCheck className="w-3 h-3" />Email</Badge>}
                      {p.recommandation_ia === 'valider' && <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px]">Recommande</Badge>}
                      {p.type_prospect === 'commune' && <Badge variant="outline" className="text-[10px]">Commune</Badge>}
                      {p.ca_estime && <Badge variant="outline" className="text-[10px] text-amber-600">{p.ca_estime}</Badge>}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.commune || '?'} ({p.departement || '?'})</span>
                      <span>{p.activite_libelle || p.activite || ''}</span>
                      {p.email_principal && <span className="flex items-center gap-1 text-violet-600 dark:text-violet-400"><Mail className="w-3 h-3" />{p.email_principal}</span>}
                    </div>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="mt-3 p-3 bg-muted/30 rounded-lg text-xs space-y-2">
                        {p.scoring?.analyse && <p><strong>Analyse IA:</strong> {p.scoring.analyse}</p>}
                        {p.scoring?.approche_suggeree && <p className="text-blue-600 dark:text-blue-400"><strong>Approche:</strong> {p.scoring.approche_suggeree}</p>}
                        {p.services_pertinents?.length > 0 && <p><strong>Services:</strong> {p.services_pertinents.join(', ')}</p>}
                        {p.scoring?.pertinence != null && (
                          <div className="flex gap-4">
                            <span>Pertinence: {p.scoring.pertinence}/30</span>
                            <span>Accessibilite: {p.scoring.accessibilite}/20</span>
                            <span>Potentiel CA: {p.scoring.potentiel_ca}/25</span>
                            <span>Closing: {p.scoring.facilite_closing}/25</span>
                          </div>
                        )}
                        {p.emails_decouverts?.length > 0 && (
                          <div><strong>Emails trouves:</strong> {p.emails_decouverts.map(e => e.email).join(', ')}</div>
                        )}
                        {p.population && <span>Population: {p.population?.toLocaleString()} hab</span>}
                        {p.surface_ha && <span> | Surface: {p.surface_ha} ha</span>}
                        {p.siret && <span className="font-mono"> | SIRET: {p.siret}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {p.statut === 'nouveau' && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => handleAction(p.id, 'valide')}
                          className="h-8 w-8 text-emerald-500 hover:bg-emerald-500/10" data-testid={`validate-${p.id}`}>
                          <ThumbsUp className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleAction(p.id, 'rejete')}
                          className="h-8 w-8 text-red-500 hover:bg-red-500/10" data-testid={`reject-${p.id}`}>
                          <ThumbsDown className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {p.statut === 'valide' && <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3 h-3 mr-1" />CRM</Badge>}
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {prospects.length === 0 && !searching && (
          <div className="text-center py-12 text-muted-foreground">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">
              {filter === 'nouveau'
                ? 'Lancez une recherche ou cliquez "Config auto" pour demarrer la prospection H24.'
                : `Aucun prospect ${filter}.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
